import { Step } from 'react-joyride'
import { trackInternalEvent } from '@rio/tracking'
import { useI18n } from 'builder/hooks/useI18n'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'

import { EditorTourStepIndex, StepName } from '../types'
import { getFloaterStepStyles, getOverlayStepStyle, TRANSLATION_KEY } from '../utils'
import { useTourConfig } from './useTourConfig'

export const useEditorTourSteps = (): Step[] => {
  const { i18n } = useI18n()
  const { isTablet, isPhone } = useMediaQueries()
  const {
    toggleTour,
    toggleDelayedTour,
    toggleAllSections,
    skipTourStep,
    setAnimationStyles,
    onUpdateDragSections,
  } = useTourConfig()

  const onSkipStep = (stepIndex: EditorTourStepIndex, stepName: StepName) => {
    trackInternalEvent('skip_rb_onboarding_hint', {
      category: 'editor',
      step: stepIndex + 1,
    })
    skipTourStep(stepName, stepIndex)
  }

  return [
    {
      offset: 5,
      disableBeacon: true,
      styles: { options: { ...getOverlayStepStyle() } },
      spotlightPadding: 0,
      spotlightClicks: true,
      target: isTablet || isPhone ? '#mobile-modal-editor-button' : '#back-to-editor-button',
      placement: 'bottom-start',
      title: i18n.t(`${TRANSLATION_KEY}.editor.step_1.title`),
      content: i18n.t(`${TRANSLATION_KEY}.editor.step_1.content`),
      floaterProps: { styles: { ...getFloaterStepStyles() } },
      data: {
        onClick: () => {
          setAnimationStyles()
          trackInternalEvent('see_rb_onboarding_hint', {
            category: 'editor',
            step: EditorTourStepIndex.EDIT_RESUME + 1,
          })
          toggleTour({
            isRunning: false,
            isStartModalOpen: false,
            stepIndex: EditorTourStepIndex.DRAG_SECTIONS,
          })
          setTimeout(() => {
            toggleAllSections(false)
            toggleDelayedTour(EditorTourStepIndex.DRAG_SECTIONS, 'editor')
          }, 500)
        },
      },
    },
    {
      offset: 10,
      disableBeacon: true,
      styles: { options: { ...getOverlayStepStyle() } },
      spotlightPadding: 5,
      spotlightClicks: true,
      showSkipButton: true,
      target: '#drag-handle-card',
      placement: 'right',
      title: i18n.t(`${TRANSLATION_KEY}.editor.step_3.title`),
      content: i18n.t(`${TRANSLATION_KEY}.editor.step_3.content`),
      floaterProps: { styles: { ...getFloaterStepStyles() } },
      data: {
        onSkip: () => {
          onSkipStep(EditorTourStepIndex.DRAG_SECTIONS, 'editor')
          onUpdateDragSections()
        },
        onClick: () => {
          setAnimationStyles()
          trackInternalEvent('see_rb_onboarding_hint', {
            category: 'editor',
            step: EditorTourStepIndex.DRAG_SECTIONS + 1,
          })
          toggleTour({
            isRunning: false,
            isStartModalOpen: false,
            stepIndex: EditorTourStepIndex.PHRASES_SUGGESTIONS,
            stepName: 'editor',
          })
        },
      },
    },
    {
      offset: 0,
      disableBeacon: true,
      styles: { options: { ...getOverlayStepStyle() } },
      spotlightPadding: 0,
      spotlightClicks: true,
      showSkipButton: true,
      disableScrolling: false,
      target: '#phrases-suggestion-toggler',
      placement: 'top',
      title: i18n.t(`${TRANSLATION_KEY}.editor.step_4.title`),
      content: i18n.t(`${TRANSLATION_KEY}.editor.step_4.content`),
      floaterProps: { styles: { ...getFloaterStepStyles() } },
      data: {
        onSkip: () => {
          setAnimationStyles()
          onSkipStep(EditorTourStepIndex.PHRASES_SUGGESTIONS, 'editor')
          toggleDelayedTour(EditorTourStepIndex.DOWNLOAD_RESUME, 'download')
        },
        onClick: () => {
          setAnimationStyles()
          trackInternalEvent('see_rb_onboarding_hint', {
            category: 'editor',
            step: EditorTourStepIndex.PHRASES_SUGGESTIONS + 1,
          })
          skipTourStep('download')
        },
      },
    },
  ]
}
